import {
  Sparkles,
  Database,
  CheckCheck,
  Truck,
  MessagesSquare,
  ScanSearch,
} from "lucide-react";

const TOOLS = [
  {
    icon: ScanSearch,
    title: "AI Product Discovery",
    body:
      "In-house AI surfaces trending products, ranks suppliers and forecasts demand before you commit. Less guesswork, faster shortlists.",
    accent: true,
  },
  {
    icon: Database,
    title: "B2B Sourcing Stack",
    body:
      "Direct factory access via 1688 and Alibaba Pro with real-time price comparison wired into our internal pipeline.",
  },
  {
    icon: Truck,
    title: "ERP & Order Management",
    body:
      "A single dashboard for every PO, every shipment, every leg — orders, freight, customs and invoicing under one ID.",
  },
  {
    icon: CheckCheck,
    title: "Digital Quality Inspection",
    body:
      "Photographic QC reports, third-party audit integrations and compliance checks delivered before the container leaves the floor.",
  },
  {
    icon: MessagesSquare,
    title: "Smart Freight Comparison",
    body:
      "Our freight engine compares routes, carriers, costs and delivery times to optimise every shipment automatically.",
  },
  {
    icon: Sparkles,
    title: "Client Portals & WhatsApp Bots",
    body:
      "Dedicated client portals, WhatsApp Business workflows and live ETAs — every update goes where you actually read it.",
  },
];

export default function Technology({ variant = "light" }) {
  const dark = variant === "dark";
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className={`relative py-14 md:py-28 overflow-hidden ${
        dark ? "bg-ink text-white" : "bg-white"
      }`}
    >
      {dark && <div className="absolute inset-0 grain opacity-30" aria-hidden />}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-end mb-14 md:mb-20">
          <div className="md:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              In-house technology · AI-powered
            </p>
            <h2
              className={`font-heading font-black tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl ${
                dark ? "text-white" : "text-ink-900"
              }`}
            >
              Sourcing,<br />
              <span className={dark ? "text-stroke-light" : "text-stroke"}>engineered.</span>
            </h2>
          </div>
          <p
            className={`md:col-span-5 text-base md:text-lg leading-relaxed ${
              dark ? "text-white/70" : "text-ink-500"
            }`}
          >
            We build our own sourcing software. AI for product discovery, ERPs
            for order tracking, digital QC, smart freight, client portals — the
            stack quietly running every shipment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const cardBase = dark
              ? "bg-ink-800/40 border-white/10 hover:border-signal/70"
              : "bg-ink-50 border-ink-100 hover:border-signal";
            const iconWrap = t.accent
              ? "bg-signal text-white border-signal"
              : dark
              ? "border-white/15 text-white"
              : "border-ink-100 text-ink-900";
            return (
              <article
                key={t.title}
                data-testid={`tech-${t.title.replace(/\s+/g, "-").toLowerCase()}`}
                className={`group p-7 md:p-8 border transition-all duration-300 hover:-translate-y-1 ${cardBase}`}
              >
                <div
                  className={`w-12 h-12 flex items-center justify-center border transition-colors ${iconWrap}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                {t.accent && (
                  <span className="mt-5 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-signal">
                    Flagship · AI
                  </span>
                )}
                <h3
                  className={`mt-${t.accent ? "2" : "5"} font-heading font-bold text-xl md:text-[22px] tracking-tight ${
                    dark ? "text-white" : "text-ink-900"
                  }`}
                >
                  {t.title}
                </h3>
                <p
                  className={`mt-3 text-[14px] leading-relaxed ${
                    dark ? "text-white/65" : "text-ink-500"
                  }`}
                >
                  {t.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
